import React, { useState } from "react";
import { useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Box,
  Chip,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
  accordion: {
    boxShadow:
      "0 1px 3px rgba(0, 0, 0, 0.2), 0 1px 1px rgba(0, 0, 0, 0.14), 0 2px 1px rgba(0, 0, 0, 0.12)",
    marginBottom: theme.spacing(1),
  },
  accordionSummary: {
    minHeight: 48,
    '&.Mui-expanded': {
      minHeight: 48,
    },
  },
  accordionDetails: {
    backgroundColor: "#ebeff3 !important",
    padding: 0,
  },
  container: {
    padding: theme.spacing(2),
    paddingTop: 0,
    marginTop: theme.spacing(-2.5),
    minHeight: "100vh",
    width: "100%",
    maxWidth: "100vw",
    boxSizing: "border-box",
  },
  testItemContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(0.5),
    },
  },
  testNameContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "flex-start",
      gap: theme.spacing(0.5),
    },
  },
  testDisplayName: {
    fontSize: "14px",
    fontWeight: "bold",
    color: theme.palette.primary.main,
    backgroundColor: "#ebeff3",
    wordBreak: "break-word",
    [theme.breakpoints.down("sm")]: {
      fontSize: "13px",
    },
  },
  testGraphName: {
    backgroundColor: "#d1ecf1",
    padding: "2px 6px",
    borderRadius: "4px",
    fontSize: "14px",
    fontWeight: "bold",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
      padding: "1px 4px",
    },
  },
  testDetails: {
    backgroundColor: "#ebeff3",
    marginTop: theme.spacing(0.5),
  },
  testDetailText: {
    fontWeight: "bold",
    [theme.breakpoints.down("sm")]: {
      fontSize: "12px",
    },
  },
  accordionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  chip: {
    [theme.breakpoints.down("sm")]: {
      minWidth: "auto",
    },
  },
}));

const AbsenteesAnalysis = ({ analysisData }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  
  // Filter tests where result is "NA" and group by test_type
  const getAbsenteesByTestType = () => {
    if (!analysisData.output.results) return {};

    const now = new Date();
    const absentees = analysisData.output.results.filter((test) => {
      return test.result === "NA" && new Date(test.endDateTime) < now;
    });

    // Group by test_type
    const groupedAbsentees = absentees.reduce((acc, test) => {
      const testType = test.test_type;
      if (!acc[testType]) {
        acc[testType] = [];
      }
      acc[testType].push(test);
      return acc;
    }, {});

    return groupedAbsentees;
  };

  const absenteesByTestType = getAbsenteesByTestType();
  const testTypes = Object.keys(absenteesByTestType);

  if (testTypes.length === 0) {
    return (
      <Box className={classes.container}>
        <Typography variant="h6" color="primary" gutterBottom>
          Absentees Analysis
        </Typography>
        <Typography variant="body1" color="textSecondary">
          No absentees found - all students have attempted their tests!
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Typography 
        variant={isMobile ? "h6" : "h5"} 
        color="primary" 
        style={{ fontWeight: "bold", marginBottom: theme.spacing(2) }}
      >
        Absent Exams
      </Typography>
      
      {testTypes.map((testType, index) => (
        <Accordion
          key={testType}
          defaultExpanded={index === 0}
          className={classes.accordion}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            className={classes.accordionSummary}
          >
            <Box className={classes.accordionTitle}>
              <Typography
                variant={isMobile ? "subtitle1" : "h6"}
                style={{ fontWeight: "bold" }}
                color="primary"
              >
                {testType}
              </Typography>
              <Chip
                label={absenteesByTestType[testType].length}
                size="small"
                color="secondary"
                className={classes.chip}
              />
            </Box>
          </AccordionSummary>
          
          <AccordionDetails className={classes.accordionDetails}>
            <List dense disablePadding style={{ width: "100%" }}>
              {absenteesByTestType[testType].map((test, testIndex) => (
                <ListItem
                  key={`${test.test_name}-${testIndex}`}
                  divider={testIndex < absenteesByTestType[testType].length - 1}
                  disableGutters
                  style={{
                    paddingTop: theme.spacing(0.5),
                    paddingBottom: theme.spacing(0.5),
                    paddingLeft: theme.spacing(1),
                    paddingRight: theme.spacing(1),
                    backgroundColor: "#ebeff3",
                  }}
                >
                  <ListItemText
                    primary={
                      <Box className={classes.testItemContainer}>
                        <Box className={classes.testNameContainer}>
                          <Typography className={classes.testDisplayName}>
                            {test.testDisplayName}
                          </Typography>
                          <Typography className={classes.testGraphName}>
                            ({test.test_graph_name})
                          </Typography>
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box className={classes.testDetails}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.testDetailText}
                        >
                          Exam Type: {test.examtype}
                        </Typography>
                        <br />
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          className={classes.testDetailText}
                        >
                          Start Date:{" "}
                          {new Date(test.startDateTime).toLocaleDateString()}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default AbsenteesAnalysis;

// import React, { useState } from "react";
// import { useMediaQuery } from "@material-ui/core";
// import { useTheme } from "@material-ui/core/styles";
// import {
//   Typography,
//   Accordion,
//   AccordionSummary,
//   AccordionDetails,
//   List,
//   ListItem,
//   ListItemText,
//   Box,
//   Chip,
// } from "@material-ui/core";
// import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
// import { makeStyles } from "@material-ui/core/styles";

// const useStyles = makeStyles(() => ({
//   accordion: {
//     boxShadow:
//       "0 1px 3px rgba(0, 0, 0, 0.2), 0 1px 1px rgba(0, 0, 0, 0.14), 0 2px 1px rgba(0, 0, 0, 0.12)",
//   },
//   accordionSummary: {
//     minHeight: 48,
//   },
//   accordionDetails: {
//     backgroundColor: "#ebeff3 !important",
//     padding: 0,
//   },
// }));

// const AbsenteesAnalysis = ({ analysisData }) => {
//   const classes = useStyles();
//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
//   // Filter tests where result is "NA" and group by test_type
//   const getAbsenteesByTestType = () => {
//     if (!analysisData.output.results) return {};

//     const now = new Date();
//     const absentees = analysisData.output.results.filter((test) => {
//       return test.result === "NA" && new Date(test.endDateTime) < now;
//     });

//     // Group by test_type
//     const groupedAbsentees = absentees.reduce((acc, test) => {
//       const testType = test.test_type;
//       if (!acc[testType]) {
//         acc[testType] = [];
//       }
//       acc[testType].push(test);
//       return acc;
//     }, {});

//     return groupedAbsentees;
//   };

//   const absenteesByTestType = getAbsenteesByTestType();
//   const testTypes = Object.keys(absenteesByTestType);

//   if (testTypes.length === 0) {
//     return (
//       <Box p={2}>
//         <Typography variant="h6" color="primary" gutterBottom>
//           Absentees Analysis
//         </Typography>
//         <Typography variant="body1" color="textSecondary">
//           No absentees found - all students have attempted their tests!
//         </Typography>
//       </Box>
//     );
//   }

//   return (
//     <Box p={2} pt={0} style={{ height: "100vh", width: "100vw" }} mt="-20px">
//       <Typography variant="h6" color="primary" style={{ fontWeight: "bold" }}>
//         Absent Exams
//       </Typography>
//       {testTypes.map((testType, index) => (
//         <Accordion
//           key={testType}
//           defaultExpanded={index === 0}
//           className={classes.accordion}
//         >
//           <AccordionSummary
//             expandIcon={<ExpandMoreIcon />}
//             className={classes.accordionSummary}
//           >
//             <Box display="flex" alignItems="center">
//               <Typography
//                 variant="h6"
//                 style={{ fontWeight: "bold" }}
//                 color="primary"
//               >
//                 {testType}
//               </Typography>
//               &nbsp;&nbsp;&nbsp;&nbsp;
//               <Chip
//                 label={absenteesByTestType[testType].length}
//                 size="small"
//                 color="secondary"
//               />
//             </Box>
//           </AccordionSummary>
//           <AccordionDetails className={classes.accordionDetails}>
//             <List dense disablePadding style={{ width: "100%" }}>
//               {absenteesByTestType[testType].map((test, testIndex) => (
//                 <ListItem
//                   key={test.test_name}
//                   divider={testIndex < absenteesByTestType[testType].length - 1}
//                   disableGutters
//                   style={{
//                     paddingTop: 4,
//                     paddingBottom: 4,
//                     backgroundColor: "#ebeff3",
//                   }}
//                 >
//                   <Box
//                     display="flex"
//                     alignItems="center"
//                     justifyContent="space-between"
//                     width="100%"
//                   >
//                     <ListItemText
//                       primary={
//                         <Box
//                           display="flex"
//                           alignItems="center"
//                           sx={{ gap: isMobile ? "20px" : "1050px" }}
//                         >
//                           <Typography
//                             fontSize="14px"
//                             style={{
//                               fontWeight: "bold",
//                               color: "primary",
//                               backgroundColor: "#ebeff3",
//                             }}
//                           >
//                             {test.testDisplayName}
//                           </Typography>
//                           <Typography
//                             variant="caption"
//                             style={{
//                               backgroundColor: "#d1ecf1",
//                               padding: "2px 6px",
//                               borderRadius: "4px",
//                               fontSize: "14px",
//                               fontWeight: "bold",
//                             }}
//                           >
//                             ({test.test_graph_name})
//                           </Typography>
//                         </Box>
//                       }
//                       secondary={
//                         <Box style={{ backgroundColor: "#ebeff3" }}>
//                           <Typography
//                             variant="caption"
//                             color="textSecondary"
//                             style={{ fontWeight: "bold" }}
//                           >
//                             Exam Type: {test.examtype}
//                           </Typography>
//                           <br />
//                           <Typography
//                             variant="caption"
//                             color="textSecondary"
//                             style={{ fontWeight: "bold" }}
//                           >
//                             Start Date:{" "}
//                             {new Date(test.startDateTime).toLocaleDateString()}
//                           </Typography>
//                         </Box>
//                       }
//                     />
//                   </Box>
//                 </ListItem>
//               ))}
//             </List>
//           </AccordionDetails>
//         </Accordion>
//       ))}
//     </Box>
//   );
// };

// export default AbsenteesAnalysis;
